import { useEffect, useState } from "react";
import api, { formatApiError, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Tag, CalendarDays } from "lucide-react";

const empty = {
  nombre: "", descripcion: "", descuento_pct: 10,
  fecha_inicio: "", fecha_fin: "",
  scope: "all", scope_value: "", activo: true,
};

export default function Promociones() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [p, prods] = await Promise.all([api.get("/promotions"), api.get("/products")]);
    setItems(p.data);
    setProducts(prods.data);
  };
  useEffect(() => { load(); }, []);

  const categorias = Array.from(new Set(products.map((p) => p.categoria).filter(Boolean)));

  const save = async () => {
    try {
      if (!form.nombre || !form.fecha_inicio || !form.fecha_fin) {
        return toast.error("Completa nombre y fechas");
      }
      const payload = { ...form, descuento_pct: Number(form.descuento_pct) };
      if (editing) await api.put(`/promotions/${editing.id}`, payload);
      else await api.post("/promotions", payload);
      toast.success(editing ? "Promoción actualizada" : "Promoción creada");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.nombre}"?`)) return;
    try { await api.delete(`/promotions/${p.id}`); toast.success("Eliminada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const isActive = (p) => {
    const today = new Date().toISOString().slice(0, 10);
    return p.activo && p.fecha_inicio <= today && p.fecha_fin >= today;
  };

  return (
    <div className="space-y-6" data-testid="promociones-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Marketing</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
            <Tag className="w-8 h-8 text-emerald-600" /> Promociones
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">
            Se aplican automáticamente al crear pedidos dentro del rango de fechas. Cuando hay múltiples aplicables, se usa el mayor descuento.
          </p>
        </div>

        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild>
            <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="bg-[#002FA7] hover:bg-[#00227A] gap-2" data-testid="new-promo-btn">
              <Plus className="w-4 h-4" /> Nueva promoción
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? "Editar promoción" : "Nueva promoción"}</DialogTitle>
              <DialogDescription>Se aplica automáticamente a los pedidos creados en el rango de fechas.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Nombre</Label>
                <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Ej. Black Friday, Fiestas Patrias..." data-testid="promo-nombre" />
              </div>
              <div>
                <Label>Descripción</Label>
                <Textarea rows={2} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Descuento %</Label>
                  <Input type="number" min={0} max={100} step="0.5" value={form.descuento_pct} onChange={(e) => setForm({ ...form, descuento_pct: e.target.value })} data-testid="promo-pct" />
                </div>
                <div>
                  <Label>Desde</Label>
                  <Input type="date" value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} data-testid="promo-desde" />
                </div>
                <div>
                  <Label>Hasta</Label>
                  <Input type="date" value={form.fecha_fin} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} data-testid="promo-hasta" />
                </div>
              </div>
              <div>
                <Label>Aplica a</Label>
                <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v, scope_value: "" })}>
                  <SelectTrigger data-testid="promo-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los productos</SelectItem>
                    <SelectItem value="categoria">Categoría específica</SelectItem>
                    <SelectItem value="producto">Producto específico</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.scope === "categoria" && (
                <div>
                  <Label>Categoría</Label>
                  <Select value={form.scope_value} onValueChange={(v) => setForm({ ...form, scope_value: v })}>
                    <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
                    <SelectContent>
                      {categorias.length === 0 && <div className="p-3 text-xs text-zinc-500">No hay categorías en tus productos.</div>}
                      {categorias.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {form.scope === "producto" && (
                <div>
                  <Label>Producto</Label>
                  <Select value={form.scope_value} onValueChange={(v) => setForm({ ...form, scope_value: v })}>
                    <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
                    <SelectContent>
                      {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex items-center gap-3 pt-2">
                <Switch checked={form.activo} onCheckedChange={(v) => setForm({ ...form, activo: v })} />
                <Label className="!m-0 text-sm">Activa</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={save} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="save-promo-btn">Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && (
          <div className="col-span-full text-center text-zinc-400 text-sm py-12 border border-dashed rounded-md">
            No hay promociones. Crea una para aplicar descuentos automáticos por temporada.
          </div>
        )}
        {items.map((p) => (
          <Card key={p.id} className="p-5 border-zinc-200 rounded-md hover:shadow-sm transition-transform duration-200 ease-out hover:-translate-y-px">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                  {p.scope === "all" ? "Todos los productos" : p.scope === "categoria" ? `Categoría: ${p.scope_value}` : `Producto específico`}
                </div>
                <div className="font-semibold mt-0.5">{p.nombre}</div>
              </div>
              <div className={`font-manrope font-black text-2xl tracking-tight ${isActive(p) ? "text-emerald-600" : "text-zinc-400"}`}>
                -{p.descuento_pct}%
              </div>
            </div>
            {p.descripcion && <div className="text-xs text-zinc-600 mb-2">{p.descripcion}</div>}
            <div className="text-xs text-zinc-500 flex items-center gap-2 border-t border-zinc-100 pt-3">
              <CalendarDays className="w-3 h-3" />
              {formatDate(p.fecha_inicio)} → {formatDate(p.fecha_fin)}
            </div>
            <div className="mt-2 text-[10px] uppercase tracking-widest font-semibold">
              {isActive(p) ? <span className="text-emerald-700">Activa ahora</span> : <span className="text-zinc-400">Inactiva</span>}
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" className="flex-1 gap-2" onClick={() => { setEditing(p); setForm({ ...empty, ...p }); setOpen(true); }}>
                <Pencil className="w-3.5 h-3.5" /> Editar
              </Button>
              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(p)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
